import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import logo from "../Images/tools-logo.jpg";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import CourseFrame from "../Components/courseframe";
import CourseButton from "../ProfileComponents/CourseButton";
import AnimatedCard, { MotionItem } from "../Components/AnimatedCard";

const EASE = [0.22, 1, 0.36, 1];

// Same fade/slide-in as MotionItem, but also staggers its own children -
// used for the tools grid so each card animates in turn
const gridContainer = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: EASE, staggerChildren: 0.08 },
  },
};

const VISIBLE_CATEGORIES = 6;

const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-[18px] w-[18px]"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const ClearIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-3.5 w-3.5"
    aria-hidden="true"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

// Same card frame + CTA button as the profile page's course cards
function ToolCard({ tool, onCategoryClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <CourseFrame tilt={false}>
      <article
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="group flex h-full flex-col rounded-2xl border border-borderColor bg-[#03163F] p-5 transition-colors duration-200 hover:border-blue-500/60 hover:bg-[#041a4a]"
      >
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-[#262C4A] bg-[#0F1631]">
            <img
              src={tool.logo || logo}
              alt=""
              className="h-full w-full object-contain p-1.5"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = logo;
              }}
            />
          </div>
          <h2 className="truncate text-base font-semibold text-white">
            {tool?.name}
          </h2>
        </div>

        {/* Description */}
        <p
          className="mb-5 flex-grow text-start text-sm leading-relaxed text-gray-300"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 4,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
          title={tool.description}
        >
          {tool.description}
        </p>

        {/* Categories */}
        {tool?.categories?.length > 0 && (
          <div className="mb-5 flex flex-wrap gap-1.5">
            {tool.categories.map((category) => (
              <button
                key={category}
                onClick={() => onCategoryClick(category)}
                className="rounded-full bg-[#163271] px-2.5 py-1 text-xs text-gray-200 transition-colors hover:bg-[#1c3f8c] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                {category}
              </button>
            ))}
          </div>
        )}

        {/* CTA - same animated button as the profile page's course cards */}
        <a
          href={tool.link}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full"
        >
          <CourseButton size="sm" label="Accéder à l'outil" forceHover={hovered} />
        </a>
      </article>
    </CourseFrame>
  );
}

const AITools = ({ setIsSidebarOpen, isSidebarOpen }) => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [aiTools, setAiTools] = useState([]);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "aitools"),
      (snapshot) => {
        setAiTools(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.error("Failed to load AI tools:", error);
      }
    );

    return () => unsubscribe();
  }, []);

  // All unique categories (fixes the old reducer that sliced on every iteration)
  const uniqueCategories = useMemo(() => {
    const set = new Set();
    aiTools.forEach((tool) =>
      (tool.categories ?? []).forEach((category) => set.add(category))
    );
    return Array.from(set);
  }, [aiTools]);

  const visibleCategories = useMemo(() => {
    if (showAllCategories) return uniqueCategories;
    const firstSix = uniqueCategories.slice(0, VISIBLE_CATEGORIES);
    // Keep the selected category visible even when the list is collapsed
    if (
      activeCategory !== "All" &&
      !firstSix.includes(activeCategory) &&
      uniqueCategories.includes(activeCategory)
    ) {
      firstSix.push(activeCategory);
    }
    return firstSix;
  }, [uniqueCategories, showAllCategories, activeCategory]);

  const filteredTools = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return aiTools.filter((tool) => {
      const categories = tool.categories ?? [];
      const matchesCategory =
        activeCategory === "All" || categories.includes(activeCategory);
      if (!matchesCategory) return false;
      if (!query) return true;

      return (
        (tool.name ?? "").toLowerCase().includes(query) ||
        (tool.description ?? "").toLowerCase().includes(query) ||
        categories.some((c) => c.toLowerCase().includes(query))
      );
    });
  }, [aiTools, activeCategory, searchQuery]);

  const hiddenCount = Math.max(uniqueCategories.length - VISIBLE_CATEGORIES, 0);
  const hasFilters = activeCategory !== "All" || searchQuery.trim() !== "";

  const resetFilters = () => {
    setActiveCategory("All");
    setSearchQuery("");
  };

  const chipBase =
    "shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#03163F]";

  return (
    <div className="flex flex-col">
      <div className="flex flex-col">
        <Header
          PageName={"Outils IA"}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      </div>

      <AnimatedCard
        tilt={false}
        effects="light"
        className="min-h-[100%] rounded-[15px] border border-borderColor bg-bgColor p-4 text-white sm:p-6 lg:p-8"
      >
        <div className="mx-auto max-w-[1600px]">
          {/* Page intro */}
          <MotionItem className="mb-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-xl text-start">
              <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
                Mes outils IA
              </h1>
              <p className="mt-3 text-base leading-relaxed text-gray-300">
                Voici les outils que tu peux utiliser pendant la formation.
              </p>
            </div>

            {/* Search */}
            <div className="group relative w-full lg:max-w-md">
              {/* Search icon */}
              <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-gray-400 transition-colors duration-300 group-focus-within:text-blue-400">
                <SearchIcon />
              </span>

              {/* Input */}
              <input
                type="text"
                placeholder="Rechercher un outil IA..."
                aria-label="Rechercher un outil IA"
                className="peer w-full border-0 border-b border-[#262C4A] bg-transparent py-3 pl-9 pr-9 text-[0.95rem] text-white placeholder-gray-400 shadow-[0_2px_4px_rgba(0,0,0,0.1)] transition-colors focus:outline-none focus:ring-0"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              {/* Animated underline */}
              <span className="pointer-events-none absolute bottom-0 left-0 h-[2px] w-0 bg-gradient-to-r from-blue-500 via-blue-400 to-cyan-400 transition-[width] duration-[400ms] ease-[cubic-bezier(0.42,0,0.58,1)] peer-focus:w-full" />

              {/* Clear button */}
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  aria-label="Effacer la recherche"
                  className="absolute inset-y-0 right-1 my-auto flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition hover:bg-[#1A2145] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  <ClearIcon />
                </button>
              )}
            </div>
          </MotionItem>

          {/* Categories */}
          <MotionItem className="mb-6 border-y border-borderColor py-4">
            <div
              className={`flex gap-2 ${showAllCategories
                  ? "flex-wrap"
                  : "flex-nowrap overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                }`}
              role="tablist"
              aria-label="Catégories"
            >
              {["All", ...visibleCategories].map((category) => {
                const isActive = activeCategory === category;
                return (
                  <button
                    key={category}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveCategory(category)}
                    className={`${chipBase} ${isActive
                        ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                        : "bg-[#163271] text-gray-200 hover:bg-[#1c3f8c]"
                      }`}
                  >
                    {category}
                  </button>
                );
              })}

              {hiddenCount > 0 && (
                <button
                  onClick={() => setShowAllCategories((prev) => !prev)}
                  className={`${chipBase} border border-[#262C4A] bg-transparent text-gray-300 hover:border-blue-500 hover:text-white`}
                >
                  {showAllCategories
                    ? "Voir moins"
                    : `Voir plus de catégories (+${hiddenCount})`}
                </button>
              )}
            </div>
          </MotionItem>

          {/* Result count + reset */}
          <MotionItem className="mb-5 flex items-center justify-between text-sm text-gray-300">
            <p aria-live="polite">
              {filteredTools.length}{" "}
              {filteredTools.length > 1 ? "outils" : "outil"}
            </p>
            {hasFilters && (
              <button
                onClick={resetFilters}
                className="rounded-md text-blue-400 underline-offset-4 transition hover:text-blue-300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                Réinitialiser les filtres
              </button>
            )}
          </MotionItem>

          {/* Empty state */}
          {filteredTools.length === 0 && (
            <MotionItem className="mx-auto flex max-w-md flex-col items-center rounded-2xl border border-dashed border-[#262C4A] bg-[#0F1631]/60 px-6 py-14 text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#163271] text-gray-200">
                <SearchIcon />
              </div>
              <p className="font-medium text-white">Aucun outil trouvé</p>
              <p className="mt-1 text-sm text-gray-300">
                Essaie un autre mot-clé ou change de catégorie.
              </p>
              {hasFilters && (
                <button
                  onClick={resetFilters}
                  className="mt-5 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F1631]"
                >
                  Réinitialiser les filtres
                </button>
              )}
            </MotionItem>
          )}

          {/* Tools grid */}
          {filteredTools.length > 0 && (
            <motion.div
              variants={gridContainer}
              className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            >
              {filteredTools.map((tool) => (
                <ToolCard key={tool.id} tool={tool} onCategoryClick={setActiveCategory} />
              ))}
            </motion.div>
          )}
        </div>
      </AnimatedCard>
    </div>
  );
};

export default AITools;